import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, message, Upload } from 'antd';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../../firebase';
import { IoMdArrowForward } from 'react-icons/io';
import { Drawer, Space } from 'antd';
import ChildRegister from './CHildRegister';
import ChildrenList from './ChildrenList';
import { analyseFace, compressPhoto, dataUrlToBlob, readFileAsDataUrl } from '../../lib/passportPhoto';
import { indexMemberFace } from '../../lib/registrationApi';

const Field = ({ name, label, disabled = false, type = "text", span = 1 }) => (
  <Form.Item
    name={name}
    label={<span className="text-xs font-semibold text-gray-400 tracking-widest uppercase">{label}</span>}
    className={span === 2 ? "col-span-1 sm:col-span-2" : "col-span-1"}
  >
    <Input
      type={type}
      disabled={disabled}
      className={`h-10 rounded-none border-0 border-b text-sm font-medium text-gray-800 px-0 focus:ring-0 ${disabled
        ? "border-gray-100 bg-transparent text-gray-400 cursor-default"
        : "border-gray-300 bg-transparent hover:border-blue-400 focus:border-blue-600"
        }`}
    />
  </Form.Item>
);

const SectionLabel = ({ title, subtitle }) => (
  <div className="col-span-1 sm:col-span-2 lg:col-span-3 mb-2 mt-6 first:mt-0">
    <p className="text-[10px] font-black tracking-[0.3em] text-blue-600 uppercase">{title}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    <div className="w-full h-px bg-gray-100 mt-3" />
  </div>
);

const Profile = () => {
  const [form] = Form.useForm();
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [profile_image, setProfile_image] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDoc = await getDoc(doc(db, 'members', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setProfileData(data);
          form.setFieldsValue(data);
        }
      }
    });
  }, [form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      let imageUrl = profileData.profile_image;
      if (profile_image) {
        const imageRef = ref(storage, `profile_images/${user.uid}`);
        await uploadBytes(imageRef, dataUrlToBlob(profile_image), { contentType: 'image/jpeg' });
        imageUrl = await getDownloadURL(imageRef);
      }
      const updatedData = { ...values, profile_image: imageUrl };
      delete updatedData.password;
      delete updatedData.confirmPassword;
      await updateDoc(doc(db, 'members', user.uid), updatedData);
      await setDoc(
        doc(db, 'public_member_profiles', user.uid),
        {
          uid: user.uid,
          first_name: updatedData.first_name || null,
          last_name: updatedData.last_name || null,
          middle_name: updatedData.middle_name || null,
          pew: updatedData.pew || null,
          office: updatedData.office || null,
          officer_at: updatedData.officer_at || null,
          profile_image: updatedData.profile_image || null,
          updated_at: new Date().toISOString(),
        },
        { merge: true }
      );
      message.success('Profile updated successfully!');
      setProfileData(updatedData);
      const newPhoto = profile_image;
      setProfile_image(null);
      (newPhoto ? analyseFace(newPhoto).catch(() => ({})) : Promise.resolve({}))
        .then(async (face) => {
          const descriptor = face.faces === 1 ? face.descriptor : undefined;
          await indexMemberFace(await user.getIdToken(), descriptor);
        })
        .catch((err) => console.warn('Face index refresh deferred:', err));
    } catch (error) {
      message.error('Error updating profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    accept: 'image/*',
    beforeUpload: (file) => {
      if (!file.type.startsWith('image/')) {
        message.error('Please choose an image file (JPG or PNG).');
        return Upload.LIST_IGNORE;
      }
      readFileAsDataUrl(file)
        .then((raw) => compressPhoto(raw))
        .then((photo) => {
          setProfile_image(photo);
          setProfileData((prev) => ({ ...prev, profile_image: photo }));
          message.info('Photo ready — press Save Changes to update your profile.');
        })
        .catch(() => message.error('Could not read that image. Please try another photo.'));
      return false;
    },
    onRemove: () => {
      setProfile_image(null);
      setProfileData((prev) => ({ ...prev, profile_image: profileData.profile_image }));
    },
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      message.success('Logged out successfully!');
      navigate('/login');
      window.location.reload();
    } catch (error) {
      message.error('Error logging out: ' + error.message);
    }
  };

  useEffect(() => {
    const loadAutocomplete = () => {
      if (window.google) {
        const fromAC = new window.google.maps.places.Autocomplete(fromInputRef.current);
        const toAC = new window.google.maps.places.Autocomplete(toInputRef.current);
        fromAC.addListener('place_changed', () => {
          const place = fromAC.getPlace();
          if (place?.formatted_address)
            setProfileData((prev) => ({ ...prev, from: place.formatted_address }));
        });
        toAC.addListener('place_changed', () => {
          const place = toAC.getPlace();
          if (place?.formatted_address)
            setProfileData((prev) => ({ ...prev, to: place.formatted_address }));
        });
      }
    };
    const id = setInterval(() => { if (window.google) { loadAutocomplete(); clearInterval(id); } }, 100);
    return () => clearInterval(id);
  }, []);

  const tabs = [
    { key: "personal", label: "Personal" },
    { key: "bethel", label: "Bethel" },
    { key: "account", label: "Account" },
  ];

  return (
    <div className="min-h-screen mt-96 bg-[#f7f8fa]">

      {/* ── Top identity bar ── */}
      <div className="bg-white border-b border-gray-100 pt-36 md:pt-40 pb-0">
        <div className="max-w-5xl mx-auto px-5 md:px-10">

          {/* Identity row */}
          <div className="flex items-end gap-5 pb-0">

            {/* Avatar */}
            <div className="relative flex-shrink-0 -mb-8">
              <div className={`w-20 h-20 rounded-full overflow-hidden border-4 border-white bg-gray-100 ${profileData.is_stakeholder ? 'ring-2 ring-green-400' : ''}`}>
                {profileData.profile_image ? (
                  <img src={profileData.profile_image} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl font-black text-gray-300">
                    {profileData.first_name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
              </div>
              {/* Upload trigger */}
              <Upload {...uploadProps} showUploadList={false} maxCount={1}>
                <button
                  type="button"
                  className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-2.5 h-2.5 text-white fill-white" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              </Upload>
            </div>

            {/* Name + meta */}
            <div className="pb-6 flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {profileData.first_name
                  ? `${profileData.first_name} ${profileData.last_name || ''}`
                  : 'Your Profile'}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                {profileData.pew && (
                  <span className="text-[10px] font-bold tracking-widest uppercase text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-sm">
                    {profileData.pew}
                  </span>
                )}
                {profileData.bethel && (
                  <span className="text-xs text-gray-400 truncate">{profileData.bethel}</span>
                )}
                {profileData.is_stakeholder && (
                  <span className="text-[10px] font-bold tracking-widest uppercase text-green-600 bg-green-50 border border-green-100 px-2 py-0.5 rounded-sm">
                    Stakeholder
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Tabs — pushed down so avatar edit icon doesn't overlap */}
          <div className="flex gap-6 mt-10 pl-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`pb-3 text-xs font-bold tracking-widest uppercase border-b-2 transition-all duration-200 ${activeTab === tab.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Form body ── */}
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-10">
        <Form form={form} layout="vertical" hideRequiredMark onFinish={onFinish}>

          {/* ── TAB: Personal ── */}
          {activeTab === "personal" && (
            <div className="bg-white border border-gray-100 rounded-sm p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1">

                <SectionLabel title="Name" subtitle="Your legal name as registered" />
                <Field name="first_name" label="First Name" />
                <Field name="last_name" label="Last Name" />
                <div className="hidden lg:block" />

                <SectionLabel title="Contact" subtitle="How we can reach you" />
                <Field name="email" label="Email Address" span={2} />
                <Field name="phone_number" label="Phone Number" type="number" />

                <SectionLabel title="Origin" subtitle="Your place of origin" />
                <Field name="residential_address" label="Residential Address" span={2} />
                <Field name="country_of_origin" label="Country" />
                <Field name="state_of_origin" label="State" />
                <Field name="lga_of_origin" label="LGA / Province" />
              </div>
            </div>
          )}

          {/* ── TAB: Bethel ── */}
          {activeTab === "bethel" && (
            <div className="bg-white border border-gray-100 rounded-sm p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1">

                <SectionLabel title="Bethel Details" subtitle="Your registered place of worship" />
                <Field name="bethel" label="Bethel Name" span={2} />
                <Field name="nation_of_worship" label="Nation of Worship" />

                <SectionLabel title="Residence" subtitle="Where you currently live" />
                <Field name="country_of_residence" label="Country" />
                <Field name="residing_state" label="State" />
                <div className="hidden lg:block" />

                <SectionLabel title="Church Standing" />
                <Field name="pew" label="Pew" />
                <Field name="next_of_kin1" label="Next of Kin" />
              </div>
            </div>
          )}

          {/* ── TAB: Account ── */}
          {activeTab === "account" && (
            <div className="space-y-4">

              {/* Children section */}
              <div className="bg-white border border-gray-100 rounded-sm p-6 md:p-8">
                <p className="text-[10px] font-black tracking-[0.3em] text-blue-600 uppercase mb-1">Children</p>
                <p className="text-xs text-gray-400 mb-5">Manage registered children under your profile</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="flex items-center gap-2 border border-blue-200 text-blue-600 hover:bg-blue-50 text-xs font-semibold px-5 py-2.5 rounded-sm transition-all duration-200"
                  >
                    + Register Child
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen2(true)}
                    className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-semibold px-5 py-2.5 rounded-sm transition-all duration-200"
                  >
                    View Children
                  </button>
                </div>
              </div>

              {/* Danger zone */}
              <div className="bg-white border border-gray-100 rounded-sm p-6 md:p-8">
                <p className="text-[10px] font-black tracking-[0.3em] text-red-500 uppercase mb-1">Session</p>
                <p className="text-xs text-gray-400 mb-5">Sign out of your current session on this device</p>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-2 border border-red-200 text-red-500 hover:bg-red-50 text-xs font-semibold px-5 py-2.5 rounded-sm transition-all duration-200"
                >
                  Sign Out
                </button>
              </div>
            </div>
          )}

          {/* ── Save bar (hidden on account tab) ── */}
          {activeTab !== "account" && (
            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-widest uppercase px-8 py-3 rounded-sm transition-all duration-200 disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save Changes"}
                <IoMdArrowForward />
              </button>
            </div>
          )}
        </Form>
      </div>

      {/* ── Drawers ── */}
      <Drawer
        title="Register Child"
        placement="bottom"
        closable
        onClose={() => setOpen(false)}
        open={open}
        extra={
          <Space>
            <button onClick={() => setOpen(false)} className="text-xs text-gray-500 border border-gray-200 px-4 py-2 rounded-sm">Cancel</button>
            <button onClick={() => setOpen(false)} className="text-xs text-white bg-blue-600 px-4 py-2 rounded-sm">OK</button>
          </Space>
        }
      >
        <ChildRegister />
      </Drawer>

      <Drawer
        title="Children List"
        width={600}
        placement="left"
        closable
        onClose={() => setOpen2(false)}
        open={open2}
        extra={
          <Space>
            <button onClick={() => setOpen2(false)} className="text-xs text-gray-500 border border-gray-200 px-4 py-2 rounded-sm">Cancel</button>
            <button onClick={() => setOpen2(false)} className="text-xs text-white bg-blue-600 px-4 py-2 rounded-sm">OK</button>
          </Space>
        }
      >
        <ChildrenList />
      </Drawer>
    </div>
  );
};

export default Profile;